/**
 * v1.2.9 — Studio Tecnico: Delega DOCFA & Planimetria Catastale.
 * Form per generare la delega in cui il datore di lavoro/proprietario
 * delega il tecnico (utente loggato) alla presentazione DOCFA e all'accesso
 * alla planimetria catastale presso l'Agenzia del Territorio.
 */
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Download, Loader2, User, Home, Briefcase, Info } from 'lucide-react';

export default function DelegaDocfaPage() {
  const [submitting, setSubmitting] = useState(false);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({
    // Dichiarante (datore di lavoro)
    dichiarante_cognome_nome: '',
    dichiarante_luogo_nascita: '',
    dichiarante_data_nascita: '',
    dichiarante_codice_fiscale: '',
    dichiarante_qualifica: 'proprietario',
    dichiarante_genere: 'M',
    // Immobile
    immobile_piano: '',
    immobile_comune: '',
    immobile_indirizzo: '',
    immobile_dati_catastali: '',
    // Tecnico
    tecnico_titolo: "l'Arch.",
    tecnico_cognome_nome: '',
    tecnico_ordine: 'Ordine degli Architetti',
    tecnico_provincia_ordine: '',
    tecnico_numero_iscrizione: '',
    tecnico_studio_indirizzo: '',
    tecnico_provincia_agenzia: '',
    // Variazione
    oggetto_variazione: 'la diversa distribuzione degli spazi interni',
  });

  useEffect(() => {
    api.get('/auth/me').then((r) => {
      setMe(r.data);
      // Pre-fill tecnico con dati utente
      setForm((f) => ({
        ...f,
        tecnico_cognome_nome: r.data.full_name || '',
      }));
    }).catch(() => {});
  }, []);

  const handleField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleGenerate = async () => {
    // Validazione minima
    const required = [
      'dichiarante_cognome_nome', 'dichiarante_luogo_nascita',
      'dichiarante_data_nascita', 'dichiarante_codice_fiscale',
      'immobile_piano', 'immobile_comune', 'immobile_indirizzo', 'immobile_dati_catastali',
      'tecnico_cognome_nome',
    ];
    const missing = required.filter((k) => !form[k] || !form[k].trim());
    if (missing.length > 0) {
      toast.error(`Campi mancanti: ${missing.join(', ')}`);
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post('/studio/deleghe/docfa-planimetria/genera', form, {
        responseType: 'blob',
      });
      // Download
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeName = (form.dichiarante_cognome_nome || 'delega').replace(/\s+/g, '_');
      a.href = url;
      a.download = `Delega_DOCFA_${safeName}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Delega DOCFA generata con successo');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione documento');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="delega-docfa-page">
      <div className="flex items-center gap-3">
        <div className="bg-amber-100 p-2 rounded-lg">
          <FileText className="w-6 h-6 text-amber-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Delega DOCFA & Planimetria Catastale</h1>
          <p className="text-sm text-slate-500">
            Genera la dichiarazione sostitutiva (Art. 47 D.P.R. 445/2000) con cui il datore di lavoro/proprietario
            delega il tecnico per la presentazione DOCFA e l&apos;accesso alla planimetria catastale.
          </p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex items-start gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          Il documento generato contiene:
          {' '}<strong>(a)</strong> i dati anagrafici del dichiarante (datore di lavoro/proprietario);
          {' '}<strong>(b)</strong> la qualità rispetto all&apos;immobile;
          {' '}<strong>(c)</strong> la delega esplicita al tecnico per presentazione DOCFA + accesso e download planimetria catastale presso l&apos;Agenzia del Territorio.
          Va firmato dal dichiarante e allegato a copia del suo documento d&apos;identità.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* SEZIONE 1 — DICHIARANTE */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="w-4 h-4 text-blue-600" /> 1. Dichiarante (Datore di lavoro)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Cognome e nome *</Label>
              <Input
                value={form.dichiarante_cognome_nome}
                onChange={(e) => handleField('dichiarante_cognome_nome', e.target.value)}
                placeholder="Es. Rossi Mario"
                data-testid="dich-cognome-nome"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Luogo di nascita *</Label>
                <Input
                  value={form.dichiarante_luogo_nascita}
                  onChange={(e) => handleField('dichiarante_luogo_nascita', e.target.value)}
                  placeholder="Es. Messina (ME)"
                  data-testid="dich-luogo"
                />
              </div>
              <div>
                <Label className="text-xs">Data di nascita *</Label>
                <Input
                  value={form.dichiarante_data_nascita}
                  onChange={(e) => handleField('dichiarante_data_nascita', e.target.value)}
                  placeholder="DD/MM/YYYY"
                  data-testid="dich-data"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Codice fiscale *</Label>
              <Input
                value={form.dichiarante_codice_fiscale}
                onChange={(e) => handleField('dichiarante_codice_fiscale', e.target.value.toUpperCase())}
                placeholder="RSSMRA80A01F158X"
                maxLength={16}
                data-testid="dich-cf"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Genere</Label>
                <Select value={form.dichiarante_genere} onValueChange={(v) => handleField('dichiarante_genere', v)}>
                  <SelectTrigger data-testid="dich-genere"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Maschile</SelectItem>
                    <SelectItem value="F">Femminile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Qualità rispetto all&apos;immobile</Label>
                <Select value={form.dichiarante_qualifica} onValueChange={(v) => handleField('dichiarante_qualifica', v)}>
                  <SelectTrigger data-testid="dich-qualifica"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="proprietario">Proprietario</SelectItem>
                    <SelectItem value="comproprietario">Comproprietario</SelectItem>
                    <SelectItem value="usufruttuario">Usufruttuario</SelectItem>
                    <SelectItem value="legale rappresentante della società proprietaria">Legale rappr. società proprietaria</SelectItem>
                    <SelectItem value="amministratore di condominio">Amministratore di condominio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SEZIONE 2 — IMMOBILE */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Home className="w-4 h-4 text-emerald-600" /> 2. Immobile oggetto della delega
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Piano *</Label>
              <Input
                value={form.immobile_piano}
                onChange={(e) => handleField('immobile_piano', e.target.value)}
                placeholder="Es. T-1-2 oppure 3"
                data-testid="imm-piano"
              />
            </div>
            <div>
              <Label className="text-xs">Comune (con sigla provincia) *</Label>
              <Input
                value={form.immobile_comune}
                onChange={(e) => handleField('immobile_comune', e.target.value)}
                placeholder="Barcellona P.G (ME)"
                data-testid="imm-comune"
              />
            </div>
            <div>
              <Label className="text-xs">Indirizzo completo *</Label>
              <Input
                value={form.immobile_indirizzo}
                onChange={(e) => handleField('immobile_indirizzo', e.target.value)}
                placeholder="via Stretto Case longo n.3"
                data-testid="imm-indirizzo"
              />
            </div>
            <div>
              <Label className="text-xs">Dati catastali (foglio/part/sub) *</Label>
              <Input
                value={form.immobile_dati_catastali}
                onChange={(e) => handleField('immobile_dati_catastali', e.target.value)}
                placeholder="fg 2 part 368 sub 7"
                data-testid="imm-catastali"
              />
            </div>
          </CardContent>
        </Card>

        {/* SEZIONE 3 — TECNICO */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Briefcase className="w-4 h-4 text-violet-600" /> 3. Tecnico delegato
              {me && <span className="text-xs font-normal text-slate-500">(precompilato dal tuo profilo)</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Titolo professionale</Label>
                <Select value={form.tecnico_titolo} onValueChange={(v) => handleField('tecnico_titolo', v)}>
                  <SelectTrigger data-testid="tec-titolo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="l'Arch.">l&apos;Arch.</SelectItem>
                    <SelectItem value="l'Ing.">l&apos;Ing.</SelectItem>
                    <SelectItem value="il Geom.">il Geom.</SelectItem>
                    <SelectItem value="il Perito Industriale">il Perito Industriale</SelectItem>
                    <SelectItem value="il Dott.">il Dott.</SelectItem>
                    <SelectItem value="lo scrivente">lo scrivente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Cognome e nome *</Label>
                <Input
                  value={form.tecnico_cognome_nome}
                  onChange={(e) => handleField('tecnico_cognome_nome', e.target.value)}
                  placeholder="Cognome Nome"
                  data-testid="tec-cognome-nome"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Ordine professionale</Label>
                <Input
                  value={form.tecnico_ordine}
                  onChange={(e) => handleField('tecnico_ordine', e.target.value)}
                  placeholder="Ordine degli Architetti"
                  data-testid="tec-ordine"
                />
              </div>
              <div>
                <Label className="text-xs">Provincia dell&apos;Ordine</Label>
                <Input
                  value={form.tecnico_provincia_ordine}
                  onChange={(e) => handleField('tecnico_provincia_ordine', e.target.value)}
                  placeholder="Messina"
                  data-testid="tec-prov-ordine"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">N. iscrizione Ordine</Label>
                <Input
                  value={form.tecnico_numero_iscrizione}
                  onChange={(e) => handleField('tecnico_numero_iscrizione', e.target.value)}
                  placeholder="2261"
                  data-testid="tec-n-iscrizione"
                />
              </div>
              <div>
                <Label className="text-xs">Sede dello studio</Label>
                <Input
                  value={form.tecnico_studio_indirizzo}
                  onChange={(e) => handleField('tecnico_studio_indirizzo', e.target.value)}
                  placeholder="Messina, via U. Bonino n.8"
                  data-testid="tec-studio"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Provincia Agenzia del Territorio</Label>
                <Input
                  value={form.tecnico_provincia_agenzia}
                  onChange={(e) => handleField('tecnico_provincia_agenzia', e.target.value)}
                  placeholder="Messina"
                  data-testid="tec-prov-agenzia"
                />
              </div>
              <div>
                <Label className="text-xs">Oggetto della variazione catastale</Label>
                <Textarea
                  value={form.oggetto_variazione}
                  onChange={(e) => handleField('oggetto_variazione', e.target.value)}
                  rows={1}
                  data-testid="oggetto-variazione"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end gap-2 sticky bottom-2 bg-white/95 backdrop-blur p-3 rounded-xl border border-slate-200 shadow-md">
        <Button
          onClick={handleGenerate}
          disabled={submitting}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="generate-btn"
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generazione...</>
          ) : (
            <><Download className="w-4 h-4 mr-2" /> Genera & Scarica Delega DOCX</>
          )}
        </Button>
      </div>
    </div>
  );
}
